import { useAtom, useAtomValue } from "jotai";
import { baseAtom } from "../stores/jotai/base";
import { useEffect, useState } from "react";
import { set } from "lodash";
import { DASHJS_EVENTS } from "../constants";
import { artifactAtom } from "../stores/jotai/artifacts/artifacts";
import { BPlayer } from "../components/discovery";
import { discoverySessionAtom } from "../stores/jotai/discovery-session";

export interface DashJSPlayerProps {
  dashJSPlayer: {
    instance: BPlayer | undefined;
    loadMediaByInterviewID: (
      interviewID: string,
      startsFrom?: number
    ) => string;
    play: (playFrom?: number, pauseTime?: number) => void;
    pause: () => void;
    reset: () => void;
    playPause: (playFrom?: number, pauseAt?: number) => void;
    isPaused: boolean;
    playBackCompleted: boolean;
  };
}

const useDashJSPlayer = (): DashJSPlayerProps => {
  const base = useAtomValue(baseAtom);
  const [pauseAt, setPauseAt] = useState<number>(null);
  const [isPaused, setIsPaused] = useState<boolean>(true);
  const [playBackCompleted, setPlayBackCompleted] = useState<boolean>(false);
  const teamID = base?.team?.groupID || null;
  const [artifacts] = useAtom(artifactAtom);

    // @ts-ignore
    const player = window?.bplayer;
    const discoverySession = useAtomValue(discoverySessionAtom);

	const loadMediaByInterviewID = (interviewID: string, startsFrom: number = 0) => {
		const vultrKey = artifacts?.media?.[interviewID]?.artifactID;
		let mediaSource = `${process.env.NEXT_PUBLIC_LINX_URL}/storage/mark/access/s3?artifactID=${vultrKey}&teamID=${teamID}&userID=${base?.user?.userID}&sessuinID=${discoverySession?.sessionID}`;

        if(artifacts?.video && artifacts?.video?.[interviewID]){
            mediaSource = `${process.env.NEXT_PUBLIC_LINX_URL}/storage/mark/access/s3?artifactID=${artifacts?.video?.[interviewID]?.artifactID}&teamID=${teamID}&userID=${base?.user?.userID}&sessuinID=${discoverySession?.sessionID}`;
        }

        player?.attachSource(mediaSource, startsFrom);
        setPlayBackCompleted(false);
		return mediaSource;
	};

    const autoPauseAt = () => {
        if (player?.time() >= pauseAt) {
            setPlayBackCompleted(true);
            pause();
        }
    }

    const pause = () => {
        player?.pause();
    };

    const play = (playFrom: number = null, pauseTime: number = null) => {
        if (!!playFrom) {
            player?.seek(playFrom);
        }

    if (!!pauseTime) {
      setPauseAt(pauseTime);
    }

        player?.play();
    }

    const playPause = (playFrom: number = null, pauseAt: number = null) => {
        if (player?.isPaused()) {
            play(playFrom, pauseAt);
        } else {
            pause();
        }
    };
    
    const onPlay = () => {
        setIsPaused(false);
    };

  const onPause = () => {
    setIsPaused(true);
  };

    const reset = () => {
        pause();
        player?.off(DASHJS_EVENTS.PLAYBACK_STARTED, onPlay);
        player?.off(DASHJS_EVENTS.PLAYBACK_PLAYING, onPlay);
        player?.off(DASHJS_EVENTS.PLAYBACK_ENDED, onPause);
        player?.off(DASHJS_EVENTS.PLAYBACK_PAUSED, onPause);
    }

    useEffect(() => {
        if (!!pauseAt) {
            player?.on("playbackTimeUpdated", autoPauseAt);
        } else {
            if (!!player && player?.isReady()) {
                player?.off("playbackTimeUpdated", autoPauseAt);
            }
        }

        return () => {
            if (!!player && player?.isReady()) {
                player?.off("playbackTimeUpdated", autoPauseAt);
            }
        }
    }, [pauseAt]);

    useEffect(() => {

        if (!!player) {
            player?.on(DASHJS_EVENTS.CAN_PLAY, () => {
                player?.on(DASHJS_EVENTS.PLAYBACK_STARTED, onPlay);
                player?.on(DASHJS_EVENTS.PLAYBACK_PLAYING, onPlay);
                player?.on(DASHJS_EVENTS.PLAYBACK_ENDED, onPause);
                player?.on(DASHJS_EVENTS.PLAYBACK_PAUSED, onPause);
            });
        }

        return () => {
            if (!!player && player?.isReady()) {
                reset();
            }
        }
    }, [])

  return {
    dashJSPlayer: {
      instance: player,
      loadMediaByInterviewID,
      play,
      pause,
      reset,
      playPause,
      isPaused,
      playBackCompleted
    }
  };
};

export default useDashJSPlayer;
